/**
 * Pinned-pass store (ADR-011) — the "passes" object store inside the existing
 * `td-v2-local` DB (db/idb.ts owns DB_NAME + DB_VERSION; the store itself is
 * created there, additively, on version upgrade — this module deliberately
 * opens the DB VERSIONLESS so exactly one place in the codebase ever names a
 * DB version; two modules racing different versions is how VersionError boot
 * failures happen).
 *
 * READ-BACK PROOF (ADR-011 mechanism 1): pinPass resolves only after the
 * raster blob has been written, read back in a fresh transaction, and its
 * header bytes decoded as a real image signature. "Pinned" is a promise made
 * at a border checkpoint — it is never reported on a write that merely
 * *seemed* to succeed (regression stance for silent blob-write failures on
 * iOS storage pressure).
 */
import { DB_NAME } from "../db/idb.ts";

export const PASSES_STORE = "passes";

export interface PassRecord {
  pass_id: string;
  gig_id: string;
  travel_item_id: string;
  person_id: string;
  /** Epoch ms of the pin. */
  pinned_at: number;
  /** The pin-time JPEG/PNG raster (parse.ts) — the display bitmap. */
  raster: Blob;
  /** Decoded barcode payload; "" when decode failed (raster ships alone). */
  barcode_text: string;
  /** zxing format name ("PDF417" | "Aztec" | "QRCode" | ...); "" when none. */
  barcode_format: string;
  /** Original file name, for provenance in the pass sheet. */
  source_name: string;
}

export interface PassStore {
  /** Write + READ-BACK PROOF. Rejects when the proof fails — the caller must
   * then report the pin as failed, never as pinned. */
  pinPass(record: PassRecord): Promise<void>;
  /** Passes for one travel leg, most recently pinned first. */
  listPassesFor(travelItemId: string): Promise<PassRecord[]>;
  deletePass(passId: string): Promise<void>;
}

/** JPEG (ff d8 ff) or PNG (89 50 4e 47) signature — the two formats parse.ts
 * ever produces. Anything else read back means the blob got corrupted or
 * swapped in storage. */
export function hasImageSignature(head: Uint8Array): boolean {
  if (head.length >= 3 && head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff) return true;
  return head.length >= 4 && head[0] === 0x89 && head[1] === 0x50 && head[2] === 0x4e && head[3] === 0x47;
}

/** The proof step, shared by real and in-memory stores: actually pull bytes
 * out of the (possibly disk-backed) blob and check the image header. */
export async function proveRasterReadable(raster: Blob): Promise<void> {
  const head = new Uint8Array(await raster.slice(0, 4).arrayBuffer());
  if (!hasImageSignature(head)) {
    throw new Error("pass raster read-back proof failed: stored blob is not a decodable image");
  }
}

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error("IndexedDB transaction aborted"));
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed"));
  });
}

export function openPassStore(): Promise<PassStore> {
  return new Promise((resolve, reject) => {
    // Versionless open: attaches at whatever version db/idb.ts last upgraded
    // to. The boot path always runs openLocalDb() first, so by pin time the
    // "passes" store exists (created in idb.ts's onupgradeneeded).
    const open = indexedDB.open(DB_NAME);
    open.onerror = () => reject(open.error ?? new Error("IndexedDB open failed"));
    open.onsuccess = () => {
      const db = open.result;
      db.onversionchange = () => db.close();
      if (!db.objectStoreNames.contains(PASSES_STORE)) {
        db.close();
        reject(new Error(`"${PASSES_STORE}" store missing — db/idb.ts DB_VERSION bump not wired`));
        return;
      }
      resolve(new IdbPassStore(db));
    };
  });
}

class IdbPassStore implements PassStore {
  constructor(private readonly db: IDBDatabase) {}

  async pinPass(record: PassRecord): Promise<void> {
    const write = this.db.transaction(PASSES_STORE, "readwrite");
    write.objectStore(PASSES_STORE).put(record);
    await txDone(write);
    // READ-BACK PROOF: a FRESH readonly transaction, so we see what a future
    // boot will see, not this transaction's in-flight view.
    const read = this.db.transaction(PASSES_STORE, "readonly");
    const stored = await requestToPromise(
      read.objectStore(PASSES_STORE).get(record.pass_id) as IDBRequest<PassRecord | undefined>,
    );
    if (stored === undefined) throw new Error("pass read-back proof failed: record missing after write");
    await proveRasterReadable(stored.raster);
  }

  async listPassesFor(travelItemId: string): Promise<PassRecord[]> {
    const tx = this.db.transaction(PASSES_STORE, "readonly");
    const all = await requestToPromise(tx.objectStore(PASSES_STORE).getAll() as IDBRequest<PassRecord[]>);
    // No index on purpose: a device holds a handful of passes; a filter beats
    // an extra upgrade-time schema surface (deletion test, ADR-014).
    return all
      .filter((p) => p.travel_item_id === travelItemId)
      .sort((a, b) => b.pinned_at - a.pinned_at);
  }

  async deletePass(passId: string): Promise<void> {
    const tx = this.db.transaction(PASSES_STORE, "readwrite");
    tx.objectStore(PASSES_STORE).delete(passId);
    await txDone(tx);
  }
}

/** In-memory PassStore — the test double (vitest runs in Node, no IndexedDB).
 * Runs the SAME read-back proof so the pin contract is testable. */
export class MemoryPassStore implements PassStore {
  private passes = new Map<string, PassRecord>();

  async pinPass(record: PassRecord): Promise<void> {
    this.passes.set(record.pass_id, record);
    const stored = this.passes.get(record.pass_id);
    if (stored === undefined) throw new Error("pass read-back proof failed: record missing after write");
    await proveRasterReadable(stored.raster);
  }

  async listPassesFor(travelItemId: string): Promise<PassRecord[]> {
    return [...this.passes.values()]
      .filter((p) => p.travel_item_id === travelItemId)
      .sort((a, b) => b.pinned_at - a.pinned_at);
  }

  async deletePass(passId: string): Promise<void> {
    this.passes.delete(passId);
  }
}
